/**
 * RoomReadinessResults
 *
 * Design: Hotel management dashboard style
 * - Clear "Guest Ready" status indicator
 * - Interactive checklist visualization
 * - Issues resolution card
 */
import React from "react";
import { RoomReadinessResults as ResultsType } from "../../types";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Bed,
  ClipboardCheck,
  Wrench,
  Search,
} from "lucide-react";

interface RoomReadinessResultsProps {
  results: ResultsType;
}

export const RoomReadinessResults: React.FC<RoomReadinessResultsProps> = ({
  results,
}) => {
  const isReady = results.room_ready;
  const score = results.readiness_score;

  const getStatusColor = () => {
    if (isReady)
      return {
        bg: "from-emerald-500/20",
        border: "border-emerald-500/30",
        text: "text-emerald-400",
        icon: <CheckCircle2 className="w-8 h-8" />,
      };
    return {
      bg: "from-red-500/20",
      border: "border-red-500/30",
      text: "text-red-400",
      icon: <XCircle className="w-8 h-8" />,
    };
  };

  const statusColor = getStatusColor();

  return (
    <div className="space-y-6">
      {/* Hero Status Card */}
      <div
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${statusColor.bg} via-slate-800/50 to-slate-900 border ${statusColor.border} p-8`}
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-slate-500/5 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div
              className={`w-20 h-20 rounded-2xl ${
                isReady ? "bg-emerald-500/20" : "bg-red-500/20"
              } flex items-center justify-center`}
            >
              {isReady ? (
                <CheckCircle2 className={`w-10 h-10 ${statusColor.text}`} />
              ) : (
                <AlertCircle className={`w-10 h-10 ${statusColor.text}`} />
              )}
            </div>
            <div>
              <p className="text-slate-400 text-sm font-medium mb-1">
                Overall Status
              </p>
              <h2 className={`text-4xl font-bold ${statusColor.text}`}>
                {isReady ? "Guest Ready" : "Attention Needed"}
              </h2>
              <p className="text-slate-400 text-sm mt-2">
                {results.room_occupied
                  ? "Room is currently occupied"
                  : isReady
                  ? "Room meets all quality standards"
                  : `${results.issues_found.length} issues require resolution`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
            <div className="text-right">
              <p className="text-3xl font-bold text-white">{score}/100</p>
              <p className="text-slate-400 text-xs text-right">
                Readiness Score
              </p>
            </div>
            <div className="w-16 h-16">
              <svg className="w-full h-full -rotate-90">
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  className="text-slate-700"
                />
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  className={isReady ? "text-emerald-500" : "text-red-500"}
                  strokeDasharray={2 * Math.PI * 28}
                  strokeDashoffset={2 * Math.PI * 28 * (1 - score / 100)}
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Checklist */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Standard Checklist</h3>
              <p className="text-slate-400 text-sm">Quality assurance items</p>
            </div>
          </div>

          <div className="space-y-3">
            {Object.entries(results.checklist).map(([item, status]) => {
              const itemReady =
                typeof status === "boolean" ? status : status === "ready";
              const label = item
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase());

              return (
                <div
                  key={item}
                  className={`flex items-center justify-between p-3 rounded-xl border ${
                    itemReady
                      ? "bg-slate-900/50 border-slate-700/50"
                      : "bg-red-500/5 border-red-500/20"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-1.5 rounded-full ${
                        itemReady ? "bg-emerald-500/20" : "bg-red-500/20"
                      }`}
                    >
                      {itemReady ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                    <span
                      className={
                        itemReady ? "text-slate-300" : "text-white font-medium"
                      }
                    >
                      {label}
                    </span>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      itemReady
                        ? "bg-emerald-500/10 text-emerald-400"
                        : "bg-red-500/10 text-red-400"
                    }`}
                  >
                    {itemReady ? "Pass" : "Fail"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Issues & Detected Objects */}
        <div className="space-y-6">
          {/* Issues Card */}
          {results.issues_found.length > 0 && (
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Required Actions</h3>
                  <p className="text-slate-400 text-sm">
                    Issues needing maintenance
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {results.issues_found.map((issue, idx) => {
                  const parts = issue.split("Solution:");
                  const problem = parts[0]?.replace("PROBLEM", "").trim();
                  const solution = parts[1]?.trim();

                  return (
                    <div
                      key={idx}
                      className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50"
                    >
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-white text-sm mb-2">
                            {problem || issue}
                          </p>
                          {solution && (
                            <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/5 px-3 py-2 rounded-lg border border-emerald-500/10">
                              <span className="font-semibold">Fix:</span>
                              {solution}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Detected Objects Mini-Card */}
          {results.detected_objects && results.detected_objects.length > 0 && (
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Search className="w-5 h-5 text-primary-400" />
                <h3 className="text-white font-semibold">Detected Items</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {results.detected_objects.map((obj, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-900/50 px-3 py-2 rounded-lg border border-slate-700/50 flex items-center gap-2"
                  >
                    <span className="text-slate-300 text-sm capitalize">
                      {obj.object.replace(/_/g, " ")}
                    </span>
                    <span className="bg-primary-500/20 text-primary-400 text-xs px-1.5 py-0.5 rounded">
                      {obj.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
